// v49.0 — TT_DB local storage shim (skeleton).
// Source: v48.311 lines 11466-13808 (~2300 lines).
// Full port deferred until /mailbox + /notifications screens migrate
// (v49.4–v49.7). For v49.0 this stub exposes the API shape so other
// modules can import it without crashing, but the actual local-storage
// persistence is a no-op until properly wired.
//
// During Phase 5 the legacy single-file index.html remains the live
// experience, so this stub only matters for `vite preview` smoke testing
// + downstream module imports. The real TT_DB instance lives in the
// legacy IIFE at labourcore-deploy/index.html and continues to handle
// all local state until v49.8 cutover.

const TABLE_KEYS = [
  "ratings", "approvedList", "discontinuedList", "appeals", "invitations",
  "mailboxes", "notifications", "walletVerifications", "walletAccessLog",
  "coordinatorRatings", "disputes", "siteOps", "subscriptions", "profiles",
  "archivedRecords", "sourcingEvents", "crewTemplates", "auditEvents",
  "contractSignatures", "contractOffers", "dealMilestones",
] as const;

type TableKey = typeof TABLE_KEYS[number];

interface DbState {
  ratings:            unknown[];
  coordinatorRatings: unknown[];
  liveRatingsCache:   unknown[];
  liveCoordinatorRatingsCache: unknown[];
  mailboxes:          Record<string, unknown>;
  notifications:      Record<string, unknown[]>;
  approvedList:       unknown[];
  discontinuedList:   unknown[];
  appeals:            unknown[];
  disputes:           unknown[];
  [k: string]:        unknown;
}

const state: DbState = {
  ratings: [], coordinatorRatings: [],
  liveRatingsCache: [], liveCoordinatorRatingsCache: [],
  mailboxes: {}, notifications: {},
  approvedList: [], discontinuedList: [], appeals: [], disputes: [],
};

/* Skeleton API matching the v48.311 TT_DB surface so call sites can
   compile during the migration window. Each method returns a sensible
   empty value until the full port lands. */
export const TT_DB = {
  /* Ratings — merged legacy + live cache surface (v48.307). */
  listRatings: () => [...state.ratings, ...state.liveRatingsCache],
  listRatingsScoped: (company?: string) => {
    if (!company) return [...state.ratings, ...state.liveRatingsCache];
    const c = company.toLowerCase();
    const matches = (r: unknown) => {
      const row = r as { ratedBy?: string; supplier?: string };
      return (row.ratedBy?.toLowerCase() === c) || (row.supplier?.toLowerCase() === c);
    };
    return [...state.ratings.filter(matches), ...state.liveRatingsCache.filter(matches)];
  },
  listCoordinatorRatings: () => [...state.coordinatorRatings, ...state.liveCoordinatorRatingsCache],
  listCoordinatorRatingsScoped: (company?: string) => {
    if (!company) return [...state.coordinatorRatings, ...state.liveCoordinatorRatingsCache];
    const c = company.toLowerCase();
    const matches = (r: unknown) => {
      const row = r as { ratedByCompany?: string; coordinatorCompany?: string };
      return (row.ratedByCompany?.toLowerCase() === c) || (row.coordinatorCompany?.toLowerCase() === c);
    };
    return [...state.coordinatorRatings.filter(matches), ...state.liveCoordinatorRatingsCache.filter(matches)];
  },
  async refreshLiveRatings() { /* full implementation deferred — Phase 5 mailbox/ratings migration. */ },

  /* Mailboxes + notifications (stubbed — real local storage layer ported later). */
  getMailbox:    (_mailId: string) => ({ inbox: [], sent: [], drafts: [] }),
  getNotifs:     (_mailId: string) => [] as unknown[],
  listMailboxes: () => ({}),

  /* Approved / discontinued / appeals / disputes. */
  listApproved:     () => [...state.approvedList],
  listDiscontinued: () => [...state.discontinuedList],
  listAppeals:      () => [...state.appeals],
  listDisputes:     () => [...state.disputes],
  listDisputesScoped: (_mailId?: string) => [] as unknown[],

  /* Profile + currentUser pointer. */
  setProfile: (_email: string, _profile: Record<string, unknown>) => { /* no-op stub */ },
  currentUser: {
    get: () => null as { email: string; role: string } | null,
    set: (_u: unknown) => { /* no-op stub */ },
    clear: () => { /* no-op stub */ },
  },

  /* Internal helpers exposed for parity with v48.311. */
  _reset: () => { Object.keys(state).forEach(k => { if (Array.isArray(state[k])) (state[k] as unknown[]).length = 0; else state[k] = {}; }); },
  _storageHealth: () => ({ usedMB: 0, capacityMB: 5, pct: 0, tables: {} }),
  _decryptOnLoad: async () => { /* no-op stub */ },

  /* Audit + appendAuditEvent fallback for v48.309's offline audit path. */
  appendAuditEvent: (_row: Record<string, unknown>) => { /* no-op stub — replaced by full port */ },
};

if (typeof window !== "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).LABOURCORE_DB = TT_DB;
}

export type { TableKey };
export { TABLE_KEYS };
export default TT_DB;
